import {useId} from 'react'
import {useQuery} from '@tanstack/react-query'
import {Shield, ClipboardList, ShieldCheck} from 'lucide-react'
import {useAuth} from '@/contexts/auth-context'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {routes} from '@/lib/routes'
import {IconText} from '@/components/shared/IconText'
import {ClickableCard} from '@/components/shared/ClickableCard'
import {StatValue} from '@/components/shared/StatValue'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {PageLayout} from '@/components/shared/PageLayout'
import {NewRequestButtons} from '@/features/my/NewRequestButtons'
import {CardContent, CardHeader, CardTitle, CardDescription} from '@/components/ui/card'
import {RecentNotifications} from '@/features/notifications/RecentNotifications'
import {AssignmentStatus} from '@/types/assignment'
import {FunctionGroupAssignmentStatus} from '@/types/function-group'
import {RequestStatus} from '@/types/request-base'
import {CONCEPT_ICON} from '@/lib/concepts'

const PENDING_REQUEST_STATUSES = `${RequestStatus.SUBMITTED},${RequestStatus.IN_PROGRESS}`

export function MyDashboard() {
    const services = useServices()
    const i18n = useI18n()
    const {user} = useAuth()
    const myAccessTitleId = useId()
    const myAccessDescId = useId()
    const myRequestsTitleId = useId()
    const myRequestsDescId = useId()
    const myApprovalsTitleId = useId()
    const myApprovalsDescId = useId()

    const {data: accessProfileAssignments} = useQuery({
        queryKey: ['my-access-count'],
        queryFn: () => services.myAccess.assignments(),
        enabled: !!user,
        refetchInterval: 30000,
    })

    const {data: fgAssignments} = useQuery({
        queryKey: ['my-fg-assignments-count'],
        queryFn: () => services.myAccess.functionGroupAssignments(),
        enabled: !!user,
        refetchInterval: 30000,
    })

    const {data: accessRequestApprovalsCount} = useQuery({
        queryKey: ['my-approvals-count'],
        queryFn: () => services.accessRequests.myApprovalsCount(),
        enabled: !!user,
        refetchInterval: 30000,
    })

    const {data: ruleRequestApprovalsCount} = useQuery({
        queryKey: ['rule-request-approvals-count'],
        queryFn: () => services.autoAssignRules.myApprovalsCount(),
        enabled: !!user,
        refetchInterval: 30000,
    })

    const {data: restrictionRuleApprovalsCount} = useQuery({
        queryKey: ['restriction-rule-request-approvals-count'],
        queryFn: () => services.restrictionRuleRequests.myApprovalsCount(),
        enabled: !!user,
        refetchInterval: 30000,
    })

    const {data: fgRequestApprovalsCount} = useQuery({
        queryKey: ['fg-request-approvals-count'],
        queryFn: () => services.functionGroupRequests.myApprovalsCount(),
        enabled: !!user,
        refetchInterval: 30000,
    })

    const approvalsCount = (accessRequestApprovalsCount ?? 0)
        + (ruleRequestApprovalsCount ?? 0)
        + (restrictionRuleApprovalsCount ?? 0)
        + (fgRequestApprovalsCount ?? 0)

    const {data: requestsPage} = useQuery({
        queryKey: ['my-requests-count', PENDING_REQUEST_STATUSES],
        queryFn: () => services.accessRequests.myRequests({page: 0, size: 1, status: PENDING_REQUEST_STATUSES}),
        enabled: !!user,
        refetchInterval: 30000,
    })

    const {data: fgRequestsPage} = useQuery({
        queryKey: ['my-fg-requests-count', PENDING_REQUEST_STATUSES],
        queryFn: () => services.functionGroupRequests.myRequests({page: 0, size: 1, status: PENDING_REQUEST_STATUSES}),
        enabled: !!user,
        refetchInterval: 30000,
    })

    // WHY: render the cards immediately and let each StatValue update as
    // its own query resolves. The previous `loading={isLoading}` aggregated
    // 8 parallel queries and gated the entire grid behind the slowest one,
    // pushing the dashboard render past the 1 s mock-mode action timeout
    // for any test that races to a card testid right after navigating in.
    // The values default to 0 via `?? 0` so the cards have stable content
    // even before the queries return.
    const activeAccessProfileCount = (accessProfileAssignments?.content ?? []).filter(a => a.status === AssignmentStatus.ACTIVE).length
    const activeFgCount = (fgAssignments?.content ?? []).filter(a => a.status === FunctionGroupAssignmentStatus.ACTIVE).length
    const activeAccessCount = activeAccessProfileCount + activeFgCount
    const pendingRequestsCount = (requestsPage?.totalElements ?? 0) + (fgRequestsPage?.totalElements ?? 0)

    return (
        <PageLayout
            variant="dashboard"
            icon={CONCEPT_ICON.dashboard}
            title={String(i18n.myDashboard.title)}
            description={String(i18n.myDashboard.subtitle)}
            actions={<NewRequestButtons/>}
            data-testid="my-dashboard-page"
        >
            <DetailGrid columns={3}>
                <ClickableCard
                    to={routes.my.access.path()}
                    data-testid="link-my-access"
                    aria-labelledby={myAccessTitleId}
                    aria-describedby={myAccessDescId}
                >
                    <CardHeader>
                        <CardTitle id={myAccessTitleId} className="text-base" data-testid="card-title-my-access">
                            <IconText icon={Shield}>{i18n.myDashboard.myAccess}</IconText>
                        </CardTitle>
                        <CardDescription id={myAccessDescId}>{i18n.myDashboard.myAccessDesc}</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <StatValue size="lg" value={activeAccessCount} data-testid="my-access-count"/>
                    </CardContent>
                </ClickableCard>

                <ClickableCard
                    to={routes.my.requests.list()}
                    data-testid="link-my-requests"
                    aria-labelledby={myRequestsTitleId}
                    aria-describedby={myRequestsDescId}
                >
                    <CardHeader>
                        <CardTitle id={myRequestsTitleId} className="text-base" data-testid="card-title-my-requests">
                            <IconText icon={ClipboardList}>{i18n.myDashboard.pendingRequests}</IconText>
                        </CardTitle>
                        <CardDescription id={myRequestsDescId}>{i18n.myDashboard.pendingRequestsDesc}</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <StatValue size="lg" value={pendingRequestsCount} data-testid="pending-requests-count"/>
                    </CardContent>
                </ClickableCard>

                <ClickableCard
                    to={routes.my.approvals.path()}
                    data-testid="link-my-approvals"
                    aria-labelledby={myApprovalsTitleId}
                    aria-describedby={myApprovalsDescId}
                >
                    <CardHeader>
                        <CardTitle id={myApprovalsTitleId} className="text-base" data-testid="card-title-my-approvals">
                            <IconText icon={ShieldCheck}>{i18n.myDashboard.pendingApprovals}</IconText>
                        </CardTitle>
                        <CardDescription id={myApprovalsDescId}>{i18n.myDashboard.pendingApprovalsDesc}</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <StatValue size="lg" value={approvalsCount} data-testid="approvals-count"/>
                    </CardContent>
                </ClickableCard>
            </DetailGrid>

            <RecentNotifications/>
        </PageLayout>
    )
}
